/**
 * Unified duration formatter (#568).
 *
 * Single source of truth for rendering a duration (in seconds) as a compact,
 * human-readable string. Replaces 7 near-duplicate local copies.
 *
 * Canonical format — "trimmed, 1-decimal seconds":
 *   5      -> "5.0s"
 *   45.7   -> "45.7s"
 *   90     -> "1m 30s"
 *   120    -> "2m"      (zero sub-units trimmed)
 *   3600   -> "1h"
 *   3930   -> "1h 5m"
 *
 * Null/empty rendering is context-dependent, so it is a per-call option
 * (table dash vs. card "0s" vs. "N/A" vs. empty string).
 */
export interface FormatDurationOptions {
  /** Returned when the value is null/undefined (and, with zeroIsEmpty, when <= 0). Default '—'. */
  emptyText?: string;
  /** When true, 0 / negative values are treated as empty (returns emptyText). Default false. */
  zeroIsEmpty?: boolean;
}

export function formatDuration(
  seconds: number | null | undefined,
  options: FormatDurationOptions = {},
): string {
  const { emptyText = '—', zeroIsEmpty = false } = options;

  if (seconds == null || (zeroIsEmpty && seconds <= 0)) {
    return emptyText;
  }

  // Sub-minute keeps one decimal of precision.
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  // For minute/hour ranges, round to whole seconds first so a value like
  // 119.6s renders "2m" rather than "1m 60s".
  const whole = Math.round(seconds);

  if (whole < 3600) {
    const m = Math.floor(whole / 60);
    const remSeconds = whole % 60;
    return remSeconds > 0 ? `${m}m ${remSeconds}s` : `${m}m`;
  }

  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
